/**
 * intercom info: https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app
 **/
import type { InjectionKey } from "vue";
import { type ClientSettings } from "@/plugins/useClientSettings";
import { Intercom, show, update, shutdown } from "@intercom/messenger-js-sdk";

interface useIntercomOptions {
   appId: string;
   isActive: boolean;
}

/**
 * Intercom Plugin
 * @param options {useIntercomOptions}
 */
class UseIntercom {
   isActive: boolean;
   appId: string | null;

   constructor(options: useIntercomOptions) {
      const { appId, isActive } = options;
      this.isActive = isActive && !!appId;
      this.appId = appId;

      if (!this.isActive) return;

      if (!this.appId) {
         console.error("Intercom appId is required");
         return;
      }
   }

   /**
    * Get the language from locale-id.
    * Language here is an ISO 639-1 two-letter code such as 'en' for English.
    * @param localeId {string} for example, 'en-gb'
    * @returns {string} language, for example, 'en'
    */
   getLanguageFromLocaleId(localeId: string): string {
      return localeId.split("-")[0];
   }

   /**
    * Boot Intercom
    * This starts the Intercom app.
    * @param email {string} user email
    * @param localeId {string} for example, 'en-gb'
    * @param intercomHash {string}
    */
   bootIntercom(email: string, localeId: string, intercomHash: string | null): void {
      if (!this.isActive) return;
      Intercom({
         app_id: this.appId,
         // we want intercom to boot in e2e so we can test however dont give it an email otherwise intercom will be fired emails
         email: (window as any).Cypress ? null : email,
         user_hash: intercomHash, // provide identify verification
         alignment: "right",
         vertical_padding: 70, // Customize vertical padding
         custom_launcher_selector: "#custom_intercom_btn",
         hide_default_launcher: true,
         language_override: this.getLanguageFromLocaleId(localeId),
      });
   }

   /**
    * The update method sends the logged-in user's details to Intercom and checks for new messages to show to the user.
    * @param email {string} user email
    * @param localeId {string} for example, 'en-gb'
    * @param intercomHash {string}
    */
   updateIntercom(email: string, localeId: string, intercomHash: string | null): void {
      // intercom will log registrations if this is set so dont do this in E2E tests
      if (!intercomInstance || !this.isActive || (!!email && !intercomHash) || (window as any).Cypress) return;
      const timeInMilliseconds = parseInt((new Date().getTime() / 1000).toString(), 10);
      update({
         email,
         user_hash: intercomHash, // provide identify verification
         last_request_at: timeInMilliseconds,
         language_override: this.getLanguageFromLocaleId(localeId),
      });
   }

   /**
    * Open Intercom
    */
   open(): void {
      if (!intercomInstance || !this.isActive) return;
      show();
   }

   /**
    * Close Intercom
    */
   close(): void {
      if (!intercomInstance) return;
      shutdown();
   }
}

/**
 * Initialize Intercom
 * @param intercomFeature
 */
let intercomInstance;
export function initIntercom(intercomFeature: ClientSettings["intercom"]): UseIntercom {
   if (intercomFeature && !import.meta.env.SSR) {
      const { isActive, appId } = intercomFeature;

      // create un-booted intercom class instance
      intercomInstance = new UseIntercom({
         appId: appId,
         isActive: isActive,
      });
   }

   return intercomInstance;
}

export const intercomKey = Symbol() as InjectionKey<UseIntercom>;

export const vueUseIntercom = {
   install: (app) => {
      app.config.globalProperties.useIntercom = intercomInstance;
      app.provide(intercomKey, intercomInstance);
   },
};

export type { UseIntercom };
